<template>
  <div class="d-flex flex-column mr-3">
    <Panel size="small" hide-header>
      <template v-slot:content>
        <div class="d-flex align-items-center mb-3 filter-pills">
          <b-button
            v-for="option in filterOptions"
            :key="option.value"
            size="sm"
            :variant="selectedFilter === option.value ? 'info' : 'outline-secondary'"
            class="mr-2 filter-pill"
            @click="selectedFilter = option.value"
          >
            {{ option.label }}
          </b-button>
        </div>
        <p class="filter-description text-muted mb-0">{{ filterDescription }}</p>
        <b-table
          :fields="fields"
          :items="filteredPayments"
          thead-class="text-nowrap"
          tbody-tr-class="text-nowrap"
        >
          <template #cell(status)="row">
            <b-badge
              tag="div"
              class="w-100 py-1"
              :variant="getBadgeVariant(row.item.paymentStatusId)"
            >
              {{ formatPaymentStatus(row.item.paymentStatusId) }}
            </b-badge>
          </template>

          <template #cell(period)="row">
            {{ `${dateFormat(row.item.billingCycleStartDate, 'MMM DD, YYYY')} - ${formatDateInclusive(row.item.billingCycleEndDate)}` }}
          </template>

          <template #cell(dueDate)="row">
            {{ formatDateInclusive(row.item.dateDue) }}
          </template>

          <template #cell(invoiceId)="row">
            <div class="text-nowrap">
              <b-button 
                v-if="row.item.invoiceId"
                variant="outline-info"
                size="sm"
                @click="getDownloadLink(row.item.invoiceId)"
              >
                {{ row.item.invoiceId }}
              </b-button>
              <span
                v-if="!row.item.invoiceId"
              >-</span>
            </div>
          </template>

          <template #cell(amount)="row">
            <div class="text-nowrap">
              {{ `${formatCurrency(row.item.amountDue)}` }}
            </div>
          </template>

          <template #cell(paymentDescription)="row">
            <div class="text-nowrap">
              {{ row.item.paymentDescription ? row.item.paymentDescription : '-' }}
            </div>
          </template>

          <template #cell(paymentDate)="row">
            {{ row.item.paymentDate ? dateFormat(row.item.paymentDate, 'MMM DD, YYYY') : '-' }}
          </template>
        </b-table>
      </template>
    </Panel>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import formatCurrency from '@/utils/formatCurrency'
import dateFormatMixin from '@/mixins/dateFormatMixin'

const statusMapping = {
  Failed: 'Failed',
  Outstanding: 'Outstanding',
  Paid: 'Paid',
  Overdue: 'Overdue',
  Cancelled: 'Cancelled',
  NotApplicable: 'Waived'
}

const statusToVariantMap = {
  Failed: 'danger',
  Outstanding: 'secondary',
  Paid: 'success',
  Overdue: 'warning',
  Cancelled: 'primary',
  NotApplicable: 'danger'
}

export default {
  name: 'Invoices',
  components: {},
  mixins: [dateFormatMixin],
  data() {
    return {
      selectedFilter: 'Pending',
      filterOptions: [
        { label: 'Pending', value: 'Pending' },
        { label: 'Overdue', value: 'Overdue' },
        { label: 'Paid', value: 'Paid' },
        { label: 'Waived', value: 'NotApplicable' },
        { label: 'All', value: 'All' }
      ],
      fields: [
        { key: 'invoiceId', label: 'Invoice #' },
        { key: 'period', label: 'Period' },
        { key: 'dueDate', label: 'Due' },
        { key: 'amount', label: 'Amount' },
        { key: 'status', label: 'Status' },
        { key: 'paymentDate', label: 'Paid' },
        { key: 'paymentDescription', label: 'Description' }
      ]
    }
  },

  computed: {
    ...mapState('billing', [
      'accountPayments'
    ]),
    filterDescription() {
      const descriptions = {
        Pending: 'Invoices that are outstanding or overdue and require payment.',
        Overdue: 'Invoices past their due date that have not been paid.',
        Paid: 'Invoices that have been fully paid.',
        NotApplicable: 'Invoices waived due to zero activity, amounts below the minimum threshold, or automatic settlement from account balance.',
        All: 'All invoices'
      }
      return descriptions[this.selectedFilter]
    },
    filteredPayments() {
      if (!this.accountPayments) return []
      if (this.selectedFilter === 'All') {
        return this.accountPayments.filter(p => p.paymentStatusId !== 'NotApplicable')
      }
      if (this.selectedFilter === 'Pending') {
        return this.accountPayments.filter(p =>
          p.paymentStatusId === 'Outstanding' || p.paymentStatusId === 'Overdue'
        )
      }
      return this.accountPayments.filter(p => p.paymentStatusId === this.selectedFilter)
    }
  },

  async created() {
    await this.getAccountPayments()
  },

  methods: {
    ...mapActions('dashboard', [
      'generateInvoiceDownloadLink'
    ]),

    ...mapActions('billing', [
      'getAccountPayments',
    ]),

    async getDownloadLink(invoiceId) {
      const downloadURL = await this.generateInvoiceDownloadLink({ invoiceId })
      window.open(downloadURL)
    },

    getBadgeVariant(status) {
      return statusToVariantMap[status]
    },

    formatPaymentStatus(status) {
      return statusMapping[status]
    },

    formatCurrency,

  }
}
</script>

<style lang="scss" scoped>
.filter-pills {
  padding: 0.5rem 0;
}

.filter-pill {
  border-radius: 20px;
  padding: 0.25rem 1rem;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
}

.filter-description {
  font-size: 0.82rem;
}
</style>
